import { useQuery } from '@tanstack/react-query';
import { RecordApprovalsPanel } from '../approvals/record-approvals-panel.js';
import { useParams } from 'react-router-dom';
import { api, type ApiError, type DealHealthResult, type GetDealBoardResult } from '@whinerp/client';
import {
  EmptyState,
  ErrorState,
  HealthChip,
  Num,
  Panel,
  SectionLabel,
  Skeleton,
  StageBar,
} from '@whinerp/ui';
import { dueStateForDate, formatMoney, formatRelativeDate } from '@whinerp/contracts/format';
import { useFormat, useT } from '../../app/i18n.js';
import { CompetitorsPanel } from './competitors-panel.js';
import { BillingPlanPanel } from './billing-plan-panel.js';
import { DealDocumentsPanel } from './deal-documents-panel.js';
import { DealPeoplePanel } from './deal-people-panel.js';
import { DealRegistrationPanel } from './deal-registration-panel.js';
import { NewQuoteButton } from './new-quote-picker.js';
import { PlaybookPanel } from './playbook-panel.js';
import { StageChecklistPanel } from './stage-checklist-panel.js';
import { defineScreen } from '../types.js';
import { RecordPage, TimelineSkeleton } from '../records/record-page.tsx';
import { SourcePanel } from '../records/source-panel.js';
import { RelationshipMapPanel } from '../records/relationship-map-panel.js';

/**
 * SC-13 Deal record.
 *
 * The stage bar and the health chip sit above the record-page pattern. The
 * health chip is a colour AND a word AND a list of what produced it - a score
 * nobody can interrogate is a score nobody acts on, and a chip that says "at
 * risk" only by being amber says nothing in a printed pipeline review.
 */

type Board = GetDealBoardResult;

function DealRecordScreen(): JSX.Element {
  const t = useT();
  const format = useFormat();
  const params = useParams();
  const dealId = params['id'] ?? '';

  // The board carries the stages and the deal's own card, which is what this
  // header needs; a dedicated getDeal would be a second answer to the same
  // question about which stage a deal is in.
  //
  // It asks for the board THIS deal is on rather than the default one. Loading
  // the default board and looking for the deal in it was correct for exactly
  // as long as the workspace had one pipeline, and then showed the not-found
  // state for every deal on any other.
  const board = useQuery<Board, ApiError>({
    queryKey: ['deal-board', 'containing', dealId],
    queryFn: () => api.getDealBoard({ query: { containingDeal: dealId } }),
  });

  const healthQuery = useQuery<DealHealthResult, ApiError>({
    queryKey: ['deal-health', dealId],
    queryFn: () => api.dealHealth({ path: { dealId } }),
  });

  const quotes = useQuery({
    queryKey: ['deal-quotes', dealId],
    queryFn: () => api.listDealQuotes({ path: { dealId } }),
  });

  if (board.isPending) return <DealRecordLoading />;
  if (board.isError) return <DealRecordError error={board.error} retry={() => void board.refetch()} />;

  const column = board.data.columns.find((c) => c.deals.some((d) => d.dealId === dealId));
  const deal = column?.deals.find((d) => d.dealId === dealId);

  if (!deal || !column) return <DealRecordEmpty />;

  /*
   * The health comes from the server (G-083).
   *
   * It used to be computed here from three fields on the card, which meant
   * every deal created this morning scored healthy and no board, list or
   * report could sort by it. Activity recency, stage age and single-threading
   * are questions about the database, and a number computed in the browser
   * exists only while somebody is looking at one record.
   */
  const health = healthQuery.data;

  return (
    <RecordPage
      objectKey="deal"
      recordId={dealId}
      title={deal.name}
      subtitle={deal.accountName ?? undefined}
      headerFieldKeys={['amount', 'close_date']}
      banner={
        <div className="flex flex-col gap-3">
          <Panel data-region="stage">
            <div className="flex flex-col gap-3">
              <StageBar
                stages={board.data.columns.map((stage) => ({
                  key: stage.stageId,
                  label: stage.label,
                  outcome: stage.outcome,
                }))}
                currentKey={column.stageId}
                labels={{
                  current: t('deal.pipeline'),
                  done: t('state.saved'),
                  progress: t('deal.pipeline'),
                }}
              />

              <div className="flex flex-wrap items-center gap-4">
                <span className="flex flex-col">
                  <SectionLabel>{t('deal.amount')}</SectionLabel>
                  <Num className="font-serif text-h2 text-ink">
                    {formatMoney(deal.amount, format, { currency: deal.currency })}
                  </Num>
                </span>

                <span className="flex flex-col">
                  <SectionLabel>{t('deal.closeDate')}</SectionLabel>
                  <Num
                    className={
                      deal.closeDate && dueStateForDate(deal.closeDate, format) === 'overdue'
                        ? 'text-body text-danger-text'
                        : 'text-body text-ink'
                    }
                  >
                    {deal.closeDate
                      ? formatRelativeDate(`${deal.closeDate}T12:00:00Z`, format)
                      : t('state.notSet')}
                  </Num>
                </span>

                {/*
                  * Colour, word and reasons.
                  *
                  * A closed deal is not SCORED - won is not healthy and lost
                  * is not at risk - but it still shows a chip, because the
                  * screen declares one and a header that loses an element on
                  * some records is a header that reads as broken. The chip
                  * says what happened instead of what might.
                  */}
                {health ? (
                  <span className="flex items-center gap-2">
                    <HealthChip
                      level={
                        health.level === 'closed'
                          ? health.outcome === 'won'
                            ? 'healthy'
                            : 'at-risk'
                          : health.level
                      }
                      label={
                        health.level === 'closed'
                          ? health.outcome === 'won'
                            ? t('deal.health.won')
                            : t('deal.health.lost')
                          : health.level === 'healthy'
                            ? t('deal.health.healthy')
                            : health.level === 'watch'
                              ? t('deal.health.watch')
                              : t('deal.health.atRisk')
                      }
                      factors={health.factors}
                      labels={{ whyThisScore: t('deal.health.why'), factors: t('deal.health.factors') }}
                    />
                    {/*
                      Stalled is its own word, beside the level rather than
                      folded into it. A deal can be at risk for reasons that
                      have nothing to do with time, and a manager sweeping for
                      deals where nothing is happening needs to see which is
                      which.
                    */}
                    {health.stalled ? (
                      <span data-deal-stalled className="rounded-chip bg-danger-tint px-1.5 text-meta text-danger-text">
                        {t('deal.health.stalled')}
                      </span>
                    ) : null}
                  </span>
                ) : null}
              </div>
            </div>
          </Panel>
        </div>
      }
      related={
        <>
        <RecordApprovalsPanel objectKey="deal" recordId={dealId} />
        <DealRegistrationPanel dealId={dealId} />
        <SourcePanel objectKey="deal" recordId={dealId} />
        <StageChecklistPanel dealId={dealId} />
        <PlaybookPanel dealId={dealId} />
        <DealPeoplePanel dealId={dealId} />
        <DealDocumentsPanel dealId={dealId} />
        <BillingPlanPanel dealId={dealId} />
        {deal.accountId ? <RelationshipMapPanel accountId={deal.accountId} dealId={dealId} /> : null}
        <CompetitorsPanel dealId={dealId} />
        <Panel data-region="quotes">
          <div className="flex flex-col gap-2">
            <span className="flex items-center gap-2">
              <SectionLabel>{t('quote.title')}</SectionLabel>
              <NewQuoteButton dealId={dealId} currency={deal.currency} />
            </span>

            {(quotes.data?.quotes ?? []).length === 0 ? (
              <p className="text-body text-ink-meta">{t('quote.empty.body')}</p>
            ) : (
              <ul className="flex flex-col">
                {(quotes.data?.quotes ?? []).map((quote) => (
                  <li
                    key={quote.quoteId}
                    data-quote={quote.quoteId}
                    className="flex items-center gap-3 border-t border-t-rule py-1 first:border-t-0"
                  >
                    <a
                      href={`/deals/${dealId}/quotes/${quote.quoteId}`}
                      className="flex-1 truncate text-body text-ink hover:text-bronze-text"
                    >
                      {quote.number}
                    </a>
                    <Num className="text-meta text-ink-meta">
                      {formatMoney(quote.total, format, { currency: quote.currency })}
                    </Num>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Panel>
        </>
      }
    />
  );
}

function DealRecordEmpty(): JSX.Element {
  const t = useT();
  return (
    <EmptyState
      reason="no-records"
      illustration="Target"
      title={t('record.notFound')}
      body={t('record.notFound.body')}
      primary={{ label: t('object.deal.plural'), href: '/deals', icon: 'Target' }}
    />
  );
}

function DealRecordLoading(): JSX.Element {
  const t = useT();
  return (
    <div className="flex w-full flex-col gap-5" role="status" aria-live="polite" aria-busy>
      <span className="sr-only">{t('state.loading')}</span>
      <Skeleton className="h-line-h1 w-block-xl" />
      <Skeleton className="h-block-xs w-full" />
      <div className="grid grid-cols-1 gap-5 xl:grid-cols-record">
        <TimelineSkeleton />
        <Skeleton className="h-block-xl w-full" />
      </div>
    </div>
  );
}

function DealRecordError({ error, retry }: { error: unknown; retry: () => void }): JSX.Element {
  return (
    <ErrorState
      title="This deal could not be loaded"
      body={error instanceof Error ? error.message : 'The record did not load.'}
      onRetry={retry}
    />
  );
}

export const screen = defineScreen({
  id: 'SC-13',
  route: '/deals/:id',
  surface: 'page',
  title: 'Deal record',
  features: ['F-040', 'F-047', 'F-019', 'F-045', 'F-043', 'F-044', 'F-056', 'F-079'],
  entitlement: 'F-040',
  reachedBy: [{ kind: 'screen', from: 'SC-12', how: 'a card on the board' }],
  components: ['RecordHeader', 'StageBar', 'HealthChip', 'Timeline', 'DetailsPanel'],
  elements: ['RecordPage'],
  sampleRecord: { objectKey: 'deal', param: 'id' },
  states: {
    empty: DealRecordEmpty,
    loading: DealRecordLoading,
    error: DealRecordError,
  },
  component: DealRecordScreen,
});

export default screen;
